import type { User } from '@ctcells/contract'
import { AUTH_STORAGE_KEYS, safeStorage, TOKEN_REFRESH_RATIO } from '@ctcells/core'

/**
 * 토큰 보관.
 *
 * | 값                | 두는 곳              | 이유                                  |
 * | ----------------- | -------------------- | ------------------------------------- |
 * | access token      | **메모리**           | TTL 900초(실측). 영속시킬 이유가 없다 |
 * | refresh token     | **`sessionStorage`** | 새로고침은 버티고, 탭을 닫으면 사라진다 |
 * | `user`(역할)      | **`sessionStorage`** | ⚠ 아래                                |
 * | `operations`      | **`sessionStorage`** | `GET /auth/me` 결과. 새로고침 뒤 첫 응답 전에도 판정이 서게 |
 *
 * 🔴 **`localStorage` 를 쓰지 않는다.** 장비 PC 는 공용 단말이다 — 브라우저를 닫아도 남는 자격은
 *    IVD 장비에서 사고다. 「메모리 전용」도 아니다: 새로고침마다 로그아웃은 현장에서 못 쓴다.
 *
 * ⚠ **`user` 를 클라이언트가 들고 있는 것은 약점이다.** JWT 에 `role` 클레임이 없고
 *    `POST /auth/refresh` 응답에 `user` 가 실리지 않는다(2026-09-17 실측). 그래서 지금은 새로고침 후
 *    역할을 복구할 방법이 저장해 둔 값뿐이었다. 🔵 2026-09-26 부터 **권한 판정의 원천은
 *    `GET /auth/me` 의 `operations` 다**(`auth/me.ts` · 로그인·부팅 복구 뒤 1회). 저장된 `user` 는
 *    그 응답이 오기 전의 대체(역할 표)와 **이름**(로그인 응답이 유일한 출처)을 위해 남는다.
 *    **저장값을 고치면 화면 노출이 바뀐다** — 다만 집行은 서버 403 이라 권한 상승은 아니다.
 */

// 🔴 키의 출처는 `@ctcells/core` 다 — 부팅 상태(로딩이냐 anonymous 냐)를 세션 스토어가
//    같은 키로 먼저 보기 때문이다. 두 곳에 따로 적으면 반드시 어긋난다.
const KEY_REFRESH = AUTH_STORAGE_KEYS.refresh
const KEY_USER = AUTH_STORAGE_KEYS.user
const KEY_SESSION = AUTH_STORAGE_KEYS.session
const KEY_OPERATIONS = AUTH_STORAGE_KEYS.operations

/** access 토큰은 모듈 변수. 새로고침하면 사라지고 refresh 로 다시 얻는다 */
let accessToken: string | null = null
/** access 토큰 만료 시각(ms epoch). 미리 갱신하는 타이머의 입력 */
let accessExpiresAt: number | null = null
/**
 * 서버가 준 수명(ms). 🔴 **「임박」의 기준을 여기서 만든다** — 남은 시간을 절대 초로 재면
 * 그 숫자가 코드에 박히고 서버 TTL 이 바뀌어도 안 따라간다. 비율로 재려면 분모가 필요하다.
 */
let accessTtlMs: number | null = null

export function getAccessToken(): string | null {
  return accessToken
}

export function getAccessExpiresAt(): number | null {
  return accessExpiresAt
}

export function setAccessToken(token: string | null, expiresInSec?: number): void {
  accessToken = token
  accessExpiresAt =
    token === null || expiresInSec === undefined ? null : Date.now() + expiresInSec * 1000
  accessTtlMs = token === null || expiresInSec === undefined ? null : expiresInSec * 1000
}

/**
 * **지금 요청을 보내기 전에 갱신해야 하는가.**
 *
 * 🔴 **401 을 「나면 고치는 것」에서 「안 나게 하는 것」으로 옮기는 자리다.** 종전에는
 *    ⓐ 부팅 직후(메모리에 access 가 없고 `sessionStorage` 에 refresh 만 있다)와
 *    ⓑ 수명이 다한 뒤에, 화면에 뜬 쿼리가 **한 벌 통째로 401** 을 받고 나서야 갱신이 돌았다
 *    (실측 2026-09-21 — `/batches`·`/recipes`·`/safety`·`/modules`·`/samples` 다섯이 401 한 벌
 *    뒤 200 한 벌). 요청이 두 배고 그 사이 화면이 한 번 빈다.
 *
 * 🔴 **셋 다 「모르면 안 한다」쪽으로 넘어진다.**
 *    - refresh 토큰이 없으면 **거짓** — 갱신할 재료가 없는데 부르면 `endSession()` 이 돌아
 *      익명 사용자가 로그아웃 경로를 탄다.
 *    - 수명을 모르면(`expires_in` 없는 응답) **거짓** — 임박인지 지어내지 않는다. 그때는
 *      종전대로 401 이 나고 `client.ts` 가 한 번 재시도한다.
 * ⚠ 임박 기준은 `TOKEN_REFRESH_RATIO` **하나**가 정한다(타이머가 쓰는 그 값) — 타이머는
 *   「80% 지나면 갱신」, 여기는 「20% 밖에 안 남았으면 갱신」으로 **같은 선**이다.
 *   두 숫자를 따로 두면 타이머가 늦게 깼을 때 어느 쪽이 맞는지 알 수 없다.
 */
export function accessTokenNeedsRefresh(now: number = Date.now()): boolean {
  if (getRefreshToken() === null) return false
  if (accessToken === null) return true
  if (accessExpiresAt === null || accessTtlMs === null) return false
  return accessExpiresAt - now <= accessTtlMs * (1 - TOKEN_REFRESH_RATIO)
}

export function getRefreshToken(): string | null {
  return safeStorage.read(KEY_REFRESH, 'session')
}

export function setRefreshToken(token: string | null): void {
  if (token === null) safeStorage.remove(KEY_REFRESH, 'session')
  else safeStorage.write(KEY_REFRESH, token, 'session')
}

export function getStoredUser(): User | null {
  return safeStorage.readJson<User>(KEY_USER, 'session')
}

export function setStoredUser(user: User | null): void {
  if (user === null) safeStorage.remove(KEY_USER, 'session')
  else safeStorage.writeJson(KEY_USER, user, 'session')
}

export function getStoredSessionId(): string | null {
  return safeStorage.read(KEY_SESSION, 'session')
}

export function setStoredSessionId(sessionId: string | null): void {
  if (sessionId === null) safeStorage.remove(KEY_SESSION, 'session')
  else safeStorage.write(KEY_SESSION, sessionId, 'session')
}

/**
 * `GET /auth/me` 의 `operations`. 🔴 모양이 틀리면 **없는 것**(`null`)이다 — 손으로 고친 저장값이
 * 배열이 아니면 역할 표로 떨어진다. ⚠ 저장값을 고치면 노출이 바뀌지만 집행은 서버 403 이다.
 */
export function getStoredOperations(): readonly string[] | null {
  const value = safeStorage.readJson<unknown>(KEY_OPERATIONS, 'session')
  return Array.isArray(value) && value.every((op) => typeof op === 'string') ? value : null
}

export function setStoredOperations(operations: readonly string[] | null): void {
  if (operations === null) safeStorage.remove(KEY_OPERATIONS, 'session')
  else safeStorage.writeJson(KEY_OPERATIONS, operations, 'session')
}

/** 전부 파기. 로그아웃 · refresh 실패 · 서버 강제 종료가 부른다 */
export function clearTokens(): void {
  setAccessToken(null)
  setRefreshToken(null)
  setStoredUser(null)
  setStoredSessionId(null)
  setStoredOperations(null)
}
